/**
 * JAISIU-future: Lit renderers for the config-form field overrides.
 *
 * Kept in a separate file so the registry (`config-form.overrides.ts`) can
 * be imported by node tests without pulling in Lit decorators.
 */

import { html, type TemplateResult } from "lit";
import {
  attachRenderers,
  normalizeUlimitValue,
  readSubagentsModel,
  readTelegramCapabilities,
  TELEGRAM_CAPABILITIES_VALUES,
  writeSubagentsModel,
  writeTelegramCapabilities,
  type FormRenderParams,
  type RenderWithLit,
} from "./config-form.overrides.ts";

const renderTelegramCapabilities: RenderWithLit = ({
  value,
  path,
  disabled,
  onPatch,
}: FormRenderParams) => {
  const current = readTelegramCapabilities(value);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Inline Buttons</label>
      <select
        class="cfg-override__select"
        ?disabled=${disabled}
        .value=${current ?? ""}
        @change=${(e: Event) => {
          const next = (e.target as HTMLSelectElement).value;
          onPatch(path, writeTelegramCapabilities(next || null));
        }}
      >
        <option value="">— disabled (array form) —</option>
        ${TELEGRAM_CAPABILITIES_VALUES.map((v) => html`<option value=${v}>${v}</option>`)}
      </select>
      <p class="cfg-override__help">
        Scope for inline buttons. Defaults to the array form when empty.
      </p>
    </div>
  `;
};

const renderTelegramCustomCommands: RenderWithLit = ({
  value,
  path,
  disabled,
  onPatch,
}: FormRenderParams) => {
  const arr = Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
  const update = (next: Array<Record<string, unknown>>) => onPatch(path, next);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Custom Commands</label>
      ${arr.map(
        (item, i) => html`
          <div class="cfg-override__row">
            <input
              type="text"
              placeholder="command"
              .value=${String(item.command ?? "")}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                const next = [...arr];
                next[i] = { ...next[i], command: (e.target as HTMLInputElement).value };
                update(next);
              }}
            />
            <input
              type="text"
              placeholder="description"
              .value=${String(item.description ?? "")}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                const next = [...arr];
                next[i] = { ...next[i], description: (e.target as HTMLInputElement).value };
                update(next);
              }}
            />
            <button
              type="button"
              class="btn"
              ?disabled=${disabled}
              @click=${() => update(arr.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          </div>
        `,
      )}
      <button
        type="button"
        class="btn"
        ?disabled=${disabled}
        @click=${() => update([...arr, { command: "", description: "" }])}
      >
        Add command
      </button>
    </div>
  `;
};

const renderTelegramAccounts: RenderWithLit = ({ value, path, disabled, onPatch }) => {
  const map =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, Record<string, unknown>>)
      : {};
  const entries = Object.entries(map);
  const update = (next: Record<string, Record<string, unknown>>) => onPatch(path, next);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Telegram Accounts</label>
      ${entries.map(
        ([id, cfg]) => html`
          <div class="cfg-override__row">
            <code class="cfg-override__id">${id}</code>
            <input
              type="text"
              placeholder="name"
              .value=${String(cfg.name ?? "")}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                const next = { ...map };
                next[id] = { ...next[id], name: (e.target as HTMLInputElement).value };
                update(next);
              }}
            />
            <label>
              <input
                type="checkbox"
                .checked=${cfg.enabled !== false}
                ?disabled=${disabled}
                @change=${(e: Event) => {
                  const next = { ...map };
                  next[id] = { ...next[id], enabled: (e.target as HTMLInputElement).checked };
                  update(next);
                }}
              />
              enabled
            </label>
            <button
              type="button"
              class="btn"
              ?disabled=${disabled}
              @click=${() => {
                const next = { ...map };
                delete next[id];
                update(next);
              }}
            >
              Remove
            </button>
          </div>
        `,
      )}
      <button
        type="button"
        class="btn"
        ?disabled=${disabled}
        @click=${() => {
          const id = `account-${Date.now()}`;
          update({ ...map, [id]: { name: "", enabled: true } });
        }}
      >
        Add account
      </button>
    </div>
  `;
};

const renderGoogleChatServiceAccount: RenderWithLit = ({ value, path, disabled, onPatch }) => {
  const hasValue = value != null && value !== "";
  const display =
    typeof value === "string"
      ? value
      : value && typeof value === "object"
        ? JSON.stringify(value, null, 2)
        : "";
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Service Account</label>
      <textarea
        class="cfg-override__textarea"
        rows="6"
        ?disabled=${disabled}
        .value=${display}
        placeholder='{"type":"service_account",...}'
        @change=${(e: Event) => {
          const raw = (e.target as HTMLTextAreaElement).value.trim();
          if (!raw) {
            onPatch(path, null);
            return;
          }
          try {
            onPatch(path, JSON.parse(raw) as unknown);
          } catch {
            onPatch(path, raw);
          }
        }}
      ></textarea>
      <p class="cfg-override__help">
        ${
          hasValue
            ? "Service account configured. Paste updated JSON to rotate, or clear the field to remove."
            : "Paste the GCP service account JSON here."
        }
      </p>
    </div>
  `;
};

const renderGoogleChatAccounts: RenderWithLit = ({ value, path, disabled, onPatch }) => {
  const map =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, Record<string, unknown>>)
      : {};
  const entries = Object.entries(map);
  const update = (next: Record<string, Record<string, unknown>>) => onPatch(path, next);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Google Chat Accounts</label>
      ${entries.map(
        ([id, cfg]) => html`
          <div class="cfg-override__row">
            <code class="cfg-override__id">${id}</code>
            <input
              type="text"
              placeholder="name"
              .value=${String(cfg.name ?? "")}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                const next = { ...map };
                next[id] = { ...next[id], name: (e.target as HTMLInputElement).value };
                update(next);
              }}
            />
            <label>
              <input
                type="checkbox"
                .checked=${cfg.enabled !== false}
                ?disabled=${disabled}
                @change=${(e: Event) => {
                  const next = { ...map };
                  next[id] = { ...next[id], enabled: (e.target as HTMLInputElement).checked };
                  update(next);
                }}
              />
              enabled
            </label>
            <button
              type="button"
              class="btn"
              ?disabled=${disabled}
              @click=${() => {
                const next = { ...map };
                delete next[id];
                update(next);
              }}
            >
              Remove
            </button>
          </div>
        `,
      )}
      <button
        type="button"
        class="btn"
        ?disabled=${disabled}
        @click=${() => {
          const id = `account-${Date.now()}`;
          update({ ...map, [id]: { name: "", enabled: true } });
        }}
      >
        Add account
      </button>
    </div>
  `;
};

const renderAgentsSubagentsModel: RenderWithLit = ({ value, path, disabled, onPatch }) => {
  const { primary, fallbacksRaw } = readSubagentsModel(value);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Subagent Model</label>
      <input
        type="text"
        placeholder="primary"
        .value=${primary}
        ?disabled=${disabled}
        @input=${(e: Event) => {
          const next = (e.target as HTMLInputElement).value.trim();
          onPatch(path, writeSubagentsModel(next, fallbacksRaw));
        }}
      />
      <input
        type="text"
        placeholder="fallbacks (comma-separated)"
        .value=${fallbacksRaw}
        ?disabled=${disabled}
        @input=${(e: Event) => {
          const next = (e.target as HTMLInputElement).value.trim();
          onPatch(path, writeSubagentsModel(primary, next));
        }}
      />
      <p class="cfg-override__help">Either a single model id, or a primary with fallback ids.</p>
    </div>
  `;
};

const renderAgentsSandboxDockerUlimits: RenderWithLit = ({ value, path, disabled, onPatch }) => {
  const map =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const update = (next: Record<string, unknown>) => onPatch(path, next);
  return html`
    <div class="cfg-override">
      <label class="cfg-override__label">Docker Ulimits</label>
      ${Object.entries(map).map(
        ([key, raw]) => html`
          <div class="cfg-override__row">
            <code class="cfg-override__id">${key}</code>
            <input
              type="text"
              placeholder="value (number or 'Name=soft:hard')"
              .value=${typeof raw === "string" ? raw : JSON.stringify(raw)}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                const next = { ...map };
                next[key] = normalizeUlimitValue(v);
                update(next);
              }}
            />
            <button
              type="button"
              class="btn"
              ?disabled=${disabled}
              @click=${() => {
                const next = { ...map };
                delete next[key];
                update(next);
              }}
            >
              Remove
            </button>
          </div>
        `,
      )}
      <button
        type="button"
        class="btn"
        ?disabled=${disabled}
        @click=${() => update({ ...map, nofile: "1024:2048" })}
      >
        Add nofile
      </button>
    </div>
  `;
};

const renderAgentsList: RenderWithLit = () => html`
  <div class="cfg-override">
    <p class="cfg-override__help">
      The Agents list is managed from the dedicated <strong>Agents</strong> panel in the sidebar.
      Use the "Add agent" button there. This entry shows the current on-disk list as a read-only
      summary.
    </p>
  </div>
`;

// Read-only status display for the setup-run snapshot.
//
// The "wizard" config block records the outcome of the last setup/install run
// (timestamp, command, commit, mode, version). It is written by the system,
// never by the form — so we render each field as a read-only status row, not
// an editable input. Showing it as form fields made it look like a wizard
// the operator needed to fill in, which is misleading.
const WIZARD_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "lastRunAt", label: "Last Run At" },
  { key: "lastRunCommand", label: "Command" },
  { key: "lastRunCommit", label: "Commit" },
  { key: "lastRunMode", label: "Mode" },
  { key: "lastRunVersion", label: "Version" },
];

function readWizardField(value: unknown, key: string): string {
  if (!value || typeof value !== "object") {
    return "—";
  }
  const v = (value as Record<string, unknown>)[key];
  if (typeof v === "string" && v.length > 0) {
    return v;
  }
  return "—";
}

const renderWizard: RenderWithLit = ({ value }: FormRenderParams) => html`
  <div class="cfg-override cfg-override--readonly">
    <p class="cfg-override__help">
      Status of the last <code>install</code> (setup) run. These fields are written by the system —
      they cannot be edited from the form. Re-run the installer to update.
    </p>
    <dl class="cfg-status-grid">
      ${WIZARD_FIELDS.map(
        (field) => html`
          <div class="cfg-status-grid__row">
            <dt class="cfg-status-grid__label">${field.label}</dt>
            <dd class="cfg-status-grid__value">${readWizardField(value, field.key)}</dd>
          </div>
        `,
      )}
    </dl>
  </div>
`;

attachRenderers({
  "telegram.capabilities": renderTelegramCapabilities,
  "telegram.customCommands": renderTelegramCustomCommands,
  "telegram.accounts": renderTelegramAccounts,
  "googlechat.serviceAccount": renderGoogleChatServiceAccount,
  "googlechat.accounts": renderGoogleChatAccounts,
  "agents.defaults.subagents.model": renderAgentsSubagentsModel,
  "agents.defaults.sandbox.docker.ulimits": renderAgentsSandboxDockerUlimits,
  "agents.list": renderAgentsList,
  wizard: renderWizard,
});

export type { FormRenderParams, TemplateResult };
